import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';

/**
 * Composable for timezone-aware date formatting.
 *
 * Uses the authenticated user's timezone preference from the database.
 * Falls back to browser timezone if not set.
 *
 * @returns {Object} Date formatting functions
 */
export function useDateFormat() {
    const page = usePage();

    /**
     * Get the user's timezone, falling back to browser timezone
     */
    const timezone = computed(() => {
        return page.props.auth?.user?.timezone
            || Intl.DateTimeFormat().resolvedOptions().timeZone
            || 'America/El_Salvador';
    });

    /**
     * Get the user's locale preference
     */
    const locale = computed(() => {
        return page.props.auth?.user?.language === 'en' ? 'en-US' : 'es-ES';
    });

    /**
     * Format a date string to a localized date (e.g., "15 ene 2024")
     * @param {string|Date} dateString - The date to format
     * @param {Object} options - Optional Intl.DateTimeFormat options
     * @returns {string} Formatted date
     */
    function formatDate(dateString, options = {}) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const defaultOptions = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: timezone.value,
        };

        return date.toLocaleDateString(locale.value, { ...defaultOptions, ...options });
    }

    /**
     * Format a date string to a localized date and time (e.g., "15 ene 2024, 14:30")
     * @param {string|Date} dateString - The date to format
     * @param {Object} options - Optional Intl.DateTimeFormat options
     * @returns {string} Formatted date and time
     */
    function formatDateTime(dateString, options = {}) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const defaultOptions = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone.value,
        };

        return date.toLocaleString(locale.value, { ...defaultOptions, ...options });
    }

    /**
     * Format a date string to time only (e.g., "14:30")
     * @param {string|Date} dateString - The date to format
     * @param {Object} options - Optional Intl.DateTimeFormat options
     * @returns {string} Formatted time
     */
    function formatTime(dateString, options = {}) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const defaultOptions = {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone.value,
        };

        return date.toLocaleTimeString(locale.value, { ...defaultOptions, ...options });
    }

    /**
     * Format a date string to a long date format (e.g., "15 de enero de 2024")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Formatted long date
     */
    function formatLongDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleDateString(locale.value, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: timezone.value,
        });
    }

    /**
     * Format a date string to month and year only (e.g., "enero 2024")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Formatted month and year
     */
    function formatMonthYear(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleDateString(locale.value, {
            month: 'long',
            year: 'numeric',
            timeZone: timezone.value,
        });
    }

    /**
     * Format a date string to a full date and time (e.g., "15 de enero de 2024, 14:30")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Formatted full date and time
     */
    function formatFullDateTime(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleString(locale.value, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone.value,
        });
    }

    /**
     * Format a date string to a relative time (e.g., "hace 5 min", "hace 2 días")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Relative time string
     */
    function formatRelative(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        const isSpanish = locale.value.startsWith('es');

        if (diffSecs < 60) {
            return isSpanish ? 'ahora' : 'now';
        }
        if (diffMins < 60) {
            return isSpanish ? `hace ${diffMins} min` : `${diffMins} min ago`;
        }
        if (diffHours < 24) {
            return isSpanish ? `hace ${diffHours}h` : `${diffHours}h ago`;
        }
        if (diffDays < 7) {
            return isSpanish ? `hace ${diffDays}d` : `${diffDays}d ago`;
        }

        // Fall back to short date for older dates
        return formatDate(dateString, { year: undefined });
    }

    /**
     * Format a date for use in form inputs (YYYY-MM-DD)
     * @param {string|Date} dateString - The date to format
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    function formatInputDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        // Format in the user's timezone
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: timezone.value,
        };

        const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(date);
        const year = parts.find(p => p.type === 'year')?.value;
        const month = parts.find(p => p.type === 'month')?.value;
        const day = parts.find(p => p.type === 'day')?.value;

        return `${year}-${month}-${day}`;
    }

    return {
        timezone,
        locale,
        formatDate,
        formatDateTime,
        formatTime,
        formatLongDate,
        formatMonthYear,
        formatFullDateTime,
        formatRelative,
        formatInputDate,
    };
}
